import { useEffect, useState } from 'react'

import { HashRouter as Router } from 'react-router-dom'

import { SecurityProvider } from '@dentalux/security'

import { Form } from '@ana/shared'
import Box from '@mui/material/Box'
import FullPageLoading from 'components/FullPageLoading'
import DatePickersUtilsProvider from 'providers/DatePickersUtilsProvider'
import NotificationMessageProvider from 'providers/NotificationMessageProvider'
import ReactQueryConfigProvider from 'providers/ReactQueryProvider'

import AnalyticsProvider from './providers/AnalyticsProvider'
import AnswersProvider from './providers/AnswersProvider'
import FactoringAgreementProvider from './providers/FactoringAgreementProvider'
import FlowEngineProvider from './providers/FlowEngineProvider'
import PatientProvider from './providers/PatientProvider'
import SourceProvider from './providers/SourceProvider'
import ThemeProvider from './providers/ThemeProvider'
import VisitedStepsProvider from './providers/VisitedStepsProvider'
import Routes from './Routes'
import * as i18n from './services/i18n'
import serverMock from './services/serverMock'

if (process.env.REACT_APP_MOCK_API) {
  serverMock()
}

type Props = {
  /* The value to initialize the answers, should only be used to simplify the tests purposes */
  initialAnswers?: Partial<Form>
  /* The value to initialize the answers, should only be used to simplify the tests purposes */
  initialVisitedSteps?: string[]
}

const App = ({ initialAnswers, initialVisitedSteps }: Props) => {
  const [loadingTranslations, setLoadingTranslations] = useState(true)

  useEffect(() => {
    i18n
      .init()
      .catch((e) => {
        console.warn({ ...e })
      })
      .finally(() => {
        setLoadingTranslations(false)
      })
  }, [setLoadingTranslations])

  if (loadingTranslations) return <Box />

  const { origin, pathname, hash } = window.location

  return (
    <ThemeProvider>
      <ReactQueryConfigProvider>
        <DatePickersUtilsProvider>
          <NotificationMessageProvider>
            <Router>
              <SourceProvider>
                <AnswersProvider initialValues={initialAnswers}>
                  <PatientProvider>
                    <FlowEngineProvider>
                      <FactoringAgreementProvider>
                        <VisitedStepsProvider initialValues={initialVisitedSteps}>
                          <AnalyticsProvider>
                            <SecurityProvider
                              config={{
                                loading: <FullPageLoading />,
                                env: process.env.REACT_APP_HADES_ENV as 'staging' | 'production',
                                redirectUri: `${origin}${pathname}${hash}`,
                                defaultLanguage: i18n.default.language as 'en' | 'de',
                                enableImpersonation: true,
                              }}
                            >
                              <Routes />
                            </SecurityProvider>
                          </AnalyticsProvider>
                        </VisitedStepsProvider>
                      </FactoringAgreementProvider>
                    </FlowEngineProvider>
                  </PatientProvider>
                </AnswersProvider>
              </SourceProvider>
            </Router>
          </NotificationMessageProvider>
        </DatePickersUtilsProvider>
      </ReactQueryConfigProvider>
    </ThemeProvider>
  )
}

export default App
